import React, { useState, useEffect, useRef, useCallback } from 'react';
import Markdown from 'markdown-to-jsx';
import { apiFetch } from './api';

// ── Markdown rendering overrides ──────────────────────────────────────────────

const markdownOptions = {
  overrides: {
    h1: { component: 'h5' },
    h2: { component: 'h5' },
    h3: { component: 'h6' },
    h4: { component: 'h6', props: { style: { fontSize: '0.85rem', fontWeight: 600 } } },
    table: { props: { className: 'table table-sm table-bordered mt-2 mb-2', style: { fontSize: '0.85rem' } } },
    thead: { props: { className: 'table-light' } },
    th: { props: { className: 'px-2 py-1' } },
    td: { props: { className: 'px-2 py-1' } },
  },
};

// ── Starter prompts per tab ───────────────────────────────────────────────────

const STARTER_PROMPTS = {
  recommended: [
    'Which initiative categories represent the largest savings opportunity?',
    'What are the top 3 initiatives by PMPM mid across all categories?',
    'How is the High-Risk Member Intervention initiative calculated?',
  ],
  cost: [
    "Where are total medical PMPMs increasing or decreasing vs last year?",
    "Which cost drivers should be prioritized for intervention?",
    "What's driving PMPM changes — utilization, unit cost, or mix?",
  ],
  member: [
    "Which member segments account for the highest total cost?",
    "How concentrated is spend among the highest-risk members?",
    "Which populations should be prioritized for care management?",
  ],
  provider: [
    "Which providers have the highest PMPM costs?",
    "How does unit price compare to network averages?",
    "Which providers represent the largest optimization opportunity?",
  ],
  predictive: [
    "Which members are most likely to incur avoidable high-cost events?",
    "What is the optimal care management strategy for at-risk members?",
    "Which high-performing specialists are accessible for steerage?",
  ],
  performance: [
    "How is each VBP program performing this year?",
    "Which programs or providers warrant contract refinement?",
    "How do savings break down by program and provider?",
  ],
  contracts: [
    "What are the key terms of this contract?",
    "How should I structure a TCOC arrangement?",
    "What benchmark PMPM is typical for this population?",
  ],
};

const TAB_LABELS = {
  cost: 'Cost',
  member: 'Member',
  provider: 'Provider',
  predictive: 'Predictive',
  performance: 'Performance',
  contracts: 'Contracts',
};

// ── Shared ChatPanel ──────────────────────────────────────────────────────────

export default function ChatPanel({ activeTab, chatStateRef, enableUpload, onUpdateCurrent, onCreateNew }) {
  const saved = chatStateRef?.current || {};
  const [messages, setMessages] = useState(saved.messages || []);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [sessionId, setSessionId] = useState(saved.sessionId || null);
  const [messagesRemaining, setMessagesRemaining] = useState(saved.messagesRemaining ?? 5);
  const [sessionExpired, setSessionExpired] = useState(saved.sessionExpired || false);

  // Session history
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(saved.showHistory || false);

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  // Persist state to ref
  useEffect(() => {
    if (chatStateRef) {
      chatStateRef.current = { messages, sessionId, messagesRemaining, sessionExpired, showHistory };
    }
  }, [messages, sessionId, messagesRemaining, sessionExpired, showHistory, chatStateRef]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  // Load session list
  const loadSessions = useCallback(async () => {
    try {
      const res = await apiFetch('/api/chat/sessions');
      const data = await res.json();
      setSessions(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (showHistory) loadSessions();
  }, [showHistory, loadSessions]);

  const stripMeta = (s) => s.replace(/\n?\[SESSION_META\].*?\[\/SESSION_META\]/, '').replace(/\n?\[SESSION_META\].*$/, '');

  const send = async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    setThinking(true);
    setShowHistory(false);

    setMessages(prev => [...prev, { role: 'user', content: text }]);

    try {
      const res = await apiFetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId, active_tab: activeTab }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let firstChunk = true;
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        accumulated += chunk;
        const display = stripMeta(accumulated);
        if (firstChunk) {
          setThinking(false);
          firstChunk = false;
          setMessages(prev => [...prev, { role: 'assistant', content: display }]);
        } else {
          setMessages(prev => {
            const updated = [...prev];
            return [...updated.slice(0, -1), { ...updated[updated.length - 1], content: display }];
          });
        }
      }

      const metaMatch = accumulated.match(/\[SESSION_META\](.*?)\[\/SESSION_META\]/);
      if (metaMatch) {
        const meta = JSON.parse(metaMatch[1]);
        setSessionId(meta.session_id);
        setMessagesRemaining(meta.messages_remaining);
        if (meta.messages_remaining === 0) setSessionExpired(true);
      }
    } catch {
      setThinking(false);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong.' }]);
    }

    setBusy(false);
    setThinking(false);
  };

  const newConversation = () => {
    setSessionId(null);
    setMessages([]);
    setMessagesRemaining(5);
    setSessionExpired(false);
    setShowHistory(false);
  };

  const switchToSession = async (sid) => {
    try {
      const res = await apiFetch(`/api/chat/sessions/${sid}`);
      const data = await res.json();
      setSessionId(sid);
      setMessages(data.messages || []);
      // Find session info from the list
      const info = sessions.find(s => s.session_id === sid);
      setMessagesRemaining(info?.messages_remaining ?? 0);
      setSessionExpired((info?.messages_remaining ?? 0) === 0);
      setShowHistory(false);
    } catch {}
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await apiFetch(`/api/chat/sessions/${sid}`, { method: 'DELETE' });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sid === sessionId) {
        newConversation();
      }
    } catch {}
  };

  const formatTime = (ts) => {
    const d = new Date(ts * 1000);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return 'Just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  const starters = STARTER_PROMPTS[activeTab] || STARTER_PROMPTS.cost;

  /* Extract "Next Natural Questions" from the end of assistant messages */
  const parseFollowUps = (text) => {
    const match = text.match(/\n---\n[\s\S]*?(?:Next Natural Questions|Follow[- ]?up)[:\s]*\n([\s\S]+)$/i)
      || text.match(/(?:\*\*Next Natural Questions:?\*\*|###?\s*Next Natural Questions:?)\s*\n([\s\S]+)$/i);
    if (!match) return { body: text, questions: [] };
    const body = text.slice(0, match.index).trimEnd();
    const questions = match[1]
      .split('\n')
      .map(l => l.replace(/^\s*\d+\.\s*/, '').replace(/^"|"$/g, '').replace(/^["\u201c]|["\u201d]$/g, '').trim())
      .filter(Boolean);
    return { body, questions };
  };

  // ── File upload (Contracts only) ──────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setBusy(true);
    setThinking(true);
    setShowHistory(false);
    setMessages(prev => [...prev, { role: 'user', content: `📎 ${file.name}`, isFile: true }]);

    const formData = new FormData();
    formData.append('file', file);

    let fullText = '';
    try {
      const res = await apiFetch('/api/contracts/extract', { method: 'POST', body: formData });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let firstChunk = true;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        fullText += chunk;
        const displayText = fullText.replace(/\n\[EXTRACT_DATA\][\s\S]*?(?:\[\/EXTRACT_DATA\])?$/, '').trimEnd();
        if (firstChunk) {
          setThinking(false);
          firstChunk = false;
          setMessages(prev => [...prev, { role: 'assistant', content: displayText }]);
        } else {
          setMessages(prev => {
            const updated = [...prev];
            return [...updated.slice(0, -1), { ...updated[updated.length - 1], content: displayText }];
          });
        }
      }
      const dataMatch = fullText.match(/\[EXTRACT_DATA\]([\s\S]*?)\[\/EXTRACT_DATA\]/);
      if (dataMatch) {
        try {
          const extracted = JSON.parse(dataMatch[1]);
          setMessages(prev => [...prev, { role: 'actions', data: extracted, handled: false }]);
        } catch { /* ignore parse error */ }
      }
    } catch {
      setThinking(false);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I could not read the document.' }]);
    }
    setBusy(false);
    setThinking(false);
  };

  const handleAction = (idx, chosen, data) => {
    setMessages(prev => prev.map((m, i) => i === idx ? { ...m, handled: true, chosen } : m));
    if (chosen === 'update') onUpdateCurrent?.(data);
    else if (chosen === 'create') onCreateNew?.(data);
  };

  return (
    <div className="d-flex flex-column h-100 p-3" style={{ background: 'var(--eh-bg-page)' }}>
      {/* Header with history toggle + new chat */}
      <div className="d-flex align-items-center justify-content-between mb-2" style={{ minHeight: 32 }}>
        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
          onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadSessions(); }}
          style={{ fontSize: '0.78rem' }}
        >
          <span style={{ fontSize: '1rem' }}>☰</span> History
        </button>
        <button
          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
          onClick={newConversation}
          style={{ fontSize: '0.78rem' }}
        >
          <span style={{ fontSize: '1rem' }}>+</span> New Chat
        </button>
      </div>

      {/* Session history list */}
      {showHistory && (
        <div className="mb-2 border rounded overflow-auto" style={{ maxHeight: 240 }}>
          {sessions.length === 0 && (
            <p className="text-muted text-center small py-3 mb-0">No conversations yet</p>
          )}
          {sessions.map(s => (
            <div
              key={s.session_id}
              className={`d-flex align-items-start gap-2 px-3 py-2 border-bottom ${s.session_id === sessionId ? 'bg-light' : ''}`}
              style={{ cursor: 'pointer', fontSize: '0.82rem' }}
              onClick={() => switchToSession(s.session_id)}
            >
              <div className="flex-grow-1 overflow-hidden">
                <div className="fw-semibold text-truncate" style={{ lineHeight: 1.3 }}>
                  {s.title}
                </div>
                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                  {TAB_LABELS[s.active_tab] || 'General'} · {s.message_count} msg · {formatTime(s.last_active)}
                </div>
              </div>
              <button
                className="btn btn-sm text-muted p-0"
                onClick={(e) => deleteSession(s.session_id, e)}
                title="Delete conversation"
                style={{ fontSize: '0.9rem', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Messages area */}
      <div className="flex-grow-1 overflow-auto mb-3 chat-messages">
        {messages.length === 0 && !showHistory && (
          enableUpload ? (
            <div className="ct-upload-prompt" onClick={() => !busy && fileInputRef.current?.click()}>
              <div className="ct-upload-icon">&#128196;</div>
              <div className="ct-upload-text">Upload a contract document</div>
              <div className="ct-upload-subtext">PDF, DOCX, or TXT — fields will be auto-populated</div>
            </div>
          ) : (
            <div className="text-center mt-5">
              <p className="text-muted mb-3">Ask a question to get started, or try one of these:</p>
              <div className="d-flex flex-column gap-2 align-items-center">
                {starters.map((prompt, i) => (
                  <button
                    key={i}
                    className="btn btn-outline-primary btn-sm rounded-pill px-3"
                    style={{ maxWidth: '90%', fontSize: '0.82rem' }}
                    onClick={() => send(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`mb-2 d-flex ${msg.role === 'user' ? 'justify-content-end' : msg.role === 'actions' ? 'justify-content-start' : 'justify-content-start'}`}
          >
            {msg.role === 'actions' ? (
              msg.handled ? (
                <div className="px-3 py-2 rounded-3 bg-light border" style={{ maxWidth: '85%', fontSize: '0.85rem', color: '#555' }}>
                  {msg.chosen === 'discard'
                    ? '✓ Extraction discarded.'
                    : msg.chosen === 'update'
                      ? '✓ Fields applied to the current contract.'
                      : '✓ New contract form created with extracted fields.'}
                </div>
              ) : (
                <div className="px-3 py-2 rounded-3 bg-light border" style={{ maxWidth: '85%' }}>
                  <div style={{ fontSize: '0.82rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                    What would you like to do with these extracted fields?
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <button className="btn btn-sm btn-primary" onClick={() => handleAction(i, 'update', msg.data)}>
                      Update current contract
                    </button>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleAction(i, 'create', msg.data)}>
                      Create new contract
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleAction(i, 'discard', msg.data)}>
                      Discard
                    </button>
                  </div>
                </div>
              )
            ) : (
              <div
                className={`px-3 py-2 rounded-3 ${
                  msg.role === 'user'
                    ? msg.isFile ? 'ct-file-bubble' : 'bg-primary text-white'
                    : 'bg-light border'
                }`}
                style={{ maxWidth: '85%', wordBreak: 'break-word' }}
              >
                {msg.role === 'assistant'
                  ? (() => {
                      const { body, questions } = parseFollowUps(msg.content);
                      const isLast = i === messages.length - 1;
                      return (
                        <>
                          <Markdown options={markdownOptions}>{body}</Markdown>
                          {isLast && !busy && !sessionExpired && questions.length > 0 && (
                            <div className="d-flex flex-column gap-1 mt-2 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                              {questions.map((q, qi) => (
                                <button
                                  key={qi}
                                  className="btn btn-outline-primary btn-sm rounded-pill px-3 text-start"
                                  style={{ fontSize: '0.8rem' }}
                                  onClick={() => send(q)}
                                >{q}</button>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()
                  : msg.content}
              </div>
            )}
          </div>
        ))}

        {thinking && (
          <div className="mb-2 d-flex justify-content-start">
            <div className="thinking-bubble px-3 py-2 rounded-3 bg-light border">
              <span className="thinking-dot" />
              <span className="thinking-dot" />
              <span className="thinking-dot" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {sessionExpired && (
        <div className="text-center mb-2">
          <p className="text-muted small mb-1">You've reached the 5-message limit for this conversation.</p>
          <button className="btn btn-outline-primary btn-sm" onClick={newConversation}>Start New Conversation</button>
        </div>
      )}

      {enableUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.doc,.txt"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
          disabled={busy}
        />
      )}

      <div className="d-flex gap-2">
        {enableUpload && (
          <button
            className="btn ct-upload-btn px-2"
            title="Upload contract document"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            &#128206;
          </button>
        )}
        <input
          className="form-control"
          type="text"
          placeholder={enableUpload ? "Ask about contract terms…" : "Type a message…"}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          disabled={busy || sessionExpired}
          maxLength={1000}
        />
        <button
          className="btn btn-primary px-3"
          onClick={() => send()}
          disabled={busy || !input.trim() || sessionExpired}
        >
          Send
        </button>
      </div>
      {!sessionExpired && (
        <div className="text-muted text-center mt-1" style={{ fontSize: '0.75rem' }}>
          {messagesRemaining} of 5 messages remaining
        </div>
      )}
    </div>
  );
}
